<template>
  <div>
    <!-- Broadcasts Table -->
    <PremiumTable
      title="Broadcast Messages"
      subtitle="Send announcements to society members instantly or on a schedule."
      :headers="headers"
      :items="broadcasts"
      :loading="loading"
      show-search
      search-placeholder="Search broadcasts..."
      show-add
      add-label="New Broadcast"
      @add="openForm"
    >
      <template v-slot:item.target_type="{ item }">
        <template v-if="item.target_type === 'all'">
          <v-chip small label color="indigo lighten-5" text-color="indigo darken-3" class="font-weight-medium">
            <v-icon left x-small>mdi-account-group-outline</v-icon>
            All Societies
          </v-chip>
        </template>
        <div v-else style="display:flex; flex-wrap:wrap; gap:4px; align-items:center;">
          <v-chip
            v-for="soc in (item.society_ids || []).slice(0, 3)"
            :key="soc._id || soc"
            small label
            color="blue lighten-5"
            text-color="blue darken-3"
            class="font-weight-medium"
          >
            <v-icon left x-small>mdi-home-city-outline</v-icon>
            {{ soc.name || soc }}
          </v-chip>
          <v-menu
            v-if="item.society_ids && item.society_ids.length > 3"
            offset-y
            bottom
            :nudge-top="-4"
            content-class="soc-overflow-menu"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-chip
                v-bind="attrs"
                v-on="on"
                small label
                color="blue lighten-4"
                text-color="blue darken-3"
                class="font-weight-bold"
                style="cursor:pointer;"
              >+{{ item.society_ids.length - 3 }}</v-chip>
            </template>
            <v-list dense class="py-1" style="min-width:160px; border-radius:10px;">
              <v-list-item
                v-for="soc in item.society_ids.slice(3)"
                :key="soc._id || soc"
                dense
                style="min-height:34px;"
              >
                <v-icon x-small class="mr-2" color="blue darken-2">mdi-home-city-outline</v-icon>
                <v-list-item-title style="font-size:13px; font-weight:500;">{{ soc.name || soc }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-chip v-if="!item.society_ids || !item.society_ids.length" small label color="blue lighten-5" text-color="blue darken-3" class="font-weight-medium">
            <v-icon left x-small>mdi-home-city-outline</v-icon>
            Specific
          </v-chip>
        </div>
      </template>

      <!-- Delivery Type -->
      <template v-slot:item.delivery_type="{ item }">
        <v-chip small label :color="item.delivery_type === 'instant' ? 'teal lighten-5' : 'orange lighten-5'"
          :text-color="item.delivery_type === 'instant' ? 'teal darken-3' : 'orange darken-3'" class="font-weight-medium">
          <v-icon left x-small>{{ item.delivery_type === 'instant' ? 'mdi-lightning-bolt' : 'mdi-clock-outline' }}</v-icon>
          {{ item.delivery_type === 'instant' ? 'Instant' : 'Scheduled' }}
        </v-chip>
      </template>

      <!-- Status -->
      <template v-slot:item.status="{ item }">
        <v-chip small label :color="statusColor(item.status)" :text-color="statusTextColor(item.status)" class="font-weight-medium">
          <v-icon left x-small>{{ statusIcon(item.status) }}</v-icon>
          {{ item.status.charAt(0).toUpperCase() + item.status.slice(1) }}
        </v-chip>
      </template>

      <!-- Scheduled Time -->
      <template v-slot:item.send_at="{ item }">
        <span v-if="item.delivery_type === 'scheduled'">{{ formatDate(item.send_at) }}</span>
        <span v-else class="grey--text text--lighten-1">—</span>
      </template>

      <!-- Sent Time / Removed per request -->

      <!-- Created At -->
      <template v-slot:item.created_at="{ item }">{{ formatDate(item.created_at) }}</template>

      <!-- Failure Reason tooltip -->
      <template v-slot:item.failure_reason="{ item }">
        <v-tooltip v-if="item.failure_reason" bottom max-width="280">
          <template v-slot:activator="{ on, attrs }">
            <v-icon v-bind="attrs" v-on="on" small color="red darken-2">mdi-alert-circle-outline</v-icon>
          </template>
          <span>{{ item.failure_reason }}</span>
        </v-tooltip>
        <span v-else class="grey--text text--lighten-1">—</span>
      </template>

      <!-- Actions -->
      <template v-slot:item.actions="{ item }">
        <v-btn
          v-if="item.status === 'pending' && item.delivery_type === 'scheduled'"
          icon
          small
          color="primary"
          @click="openForm(item)"
          title="Edit Broadcast"
        >
          <v-icon small>mdi-pencil</v-icon>
        </v-btn>
        <v-btn
          icon
          small
          color="error"
          @click="confirmDelete(item)"
          title="Delete Broadcast"
        >
          <v-icon small>mdi-delete</v-icon>
        </v-btn>
      </template>
    </PremiumTable>

    <!-- Create / Edit Broadcast Dialog -->
    <PremiumModalForm
      v-model="formDialog"
      :title="isEditing ? 'Edit Broadcast Message' : 'New Broadcast Message'"
      :submit-text="isEditing ? 'Update Broadcast' : 'Send Broadcast'"
      cancel-text="Cancel"
      :loading="saving"
      max-width="600"
      @submit="saveBroadcast"
    >
      <div style="display:flex;flex-direction:column;gap:18px;">
        <!-- Title -->
        <PremiumInput
          v-model="form.title"
          name="title"
          label="Title *"
          placeholder="e.g. Maintenance Notice"
          icon="mdi-format-title"
          v-validate="'required'"
          :error="errors.first('title') || serverSideErrors.title"
        />

        <!-- Description -->
        <PremiumInput
          v-model="form.description"
          name="description"
          label="Description *"
          placeholder="Enter your broadcast message..."
          icon="mdi-text"
          multiline
          rows="4"
          v-validate="'required'"
          :error="errors.first('description') || serverSideErrors.description"
        />

        <!-- Target Audience -->
        <div>
          <div class="form-section-label mb-2">Target Audience</div>
          <div style="display:flex;gap:12px;">
            <div
              v-for="opt in targetOptions"
              :key="opt.value"
              class="target-option"
              :class="{ 'target-option--active': form.target_type === opt.value }"
              @click="form.target_type = opt.value; form.society_ids = []"
            >
              <v-icon size="20" class="mr-2">{{ opt.icon }}</v-icon>
              {{ opt.label }}
            </div>
          </div>
        </div>

        <PremiumInput
          v-if="form.target_type === 'specific'"
          v-model="form.society_ids"
          name="society_ids"
          label="Select Societies *"
          placeholder="Search and choose societies"
          icon="mdi-home-city-outline"
          is-select
          searchable
          multiple
          chips
          deletable-chips
          :items="societies"
          item-text="name"
          item-value="id"
          v-validate="form.target_type === 'specific' ? 'required' : ''"
          data-vv-as="Society"
          :error="errors.first('society_ids') || serverSideErrors.society_ids"
        />

        <!-- Delivery Timing -->
        <div>
          <div class="form-section-label mb-2">Delivery Timing</div>
          <div style="display:flex;gap:12px;">
            <div
              v-for="opt in deliveryOptions"
              :key="opt.value"
              class="target-option"
              :class="{ 'target-option--active': form.delivery_type === opt.value }"
              @click="form.delivery_type = opt.value; form.send_at = null"
            >
              <v-icon size="20" class="mr-2">{{ opt.icon }}</v-icon>
              {{ opt.label }}
            </div>
          </div>
        </div>

        <!-- DateTime picker (scheduled only) -->
        <div v-if="form.delivery_type === 'scheduled'">
          <label class="form-section-label d-block mb-1">Schedule Date & Time *</label>
          <v-menu
            ref="dateMenu"
            v-model="dateMenu"
            :close-on-content-click="false"
            transition="slide-y-transition"
            offset-y
            min-width="auto"
            content-class="ism-custom-datepicker-menu"
          >
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on" style="display:flex; align-items:center; border:1px solid var(--border-color, #e2e8f0); border-radius:10px; padding:10px 14px; background:var(--bg-primary, #fff); cursor:pointer; transition: all 0.2s ease;" :style="serverSideErrors.send_at ? 'border-color: #ef4444;' : ''">
                <v-icon size="18" color="var(--accent)" class="mr-2">mdi-calendar-clock</v-icon>
                <span style="font-size:14px; color:var(--text-primary); font-weight:500;">
                  <template v-if="scheduleDate && scheduleTime">{{ formattedScheduleDate }}, {{ formattedScheduleTime }}</template>
                  <template v-else-if="scheduleDate">{{ formattedScheduleDate }} (Select Time)</template>
                  <template v-else>Select Date & Time</template>
                </span>
                <v-icon size="18" color="var(--text-secondary)" class="ml-auto">mdi-chevron-down</v-icon>
              </div>
            </template>
            
            <div style="display:flex; flex-direction:column; background:var(--bg-primary, #fff);">
              <div style="display:flex; flex-wrap: wrap;">
                <!-- Date Picker -->
                <v-date-picker
                  v-model="scheduleDate"
                  no-title
                  :min="minDateOnly"
                  scrollable
                  color="#F26522"
                  header-color="#F26522"
                  class="ism-custom-datepicker"
                  style="border-right: 1px solid var(--border-color, #e2e8f0); border-radius: 0; box-shadow: none;"
                ></v-date-picker>

                <!-- Time Picker -->
                <v-time-picker
                  v-model="scheduleTime"
                  no-title
                  color="#F26522"
                  header-color="#F26522"
                  scrollable
                  class="ism-custom-datepicker"
                  style="border-radius: 0; box-shadow: none;"
                ></v-time-picker>
              </div>
              
              <div class="v-picker__actions" style="display:flex; justify-content:flex-end; padding:8px 12px 12px; border-top:1px solid var(--border-color, #e2e8f0);">
                <v-btn text color="var(--text-secondary)" class="ism-dp-btn" @click="dateMenu = false">Cancel</v-btn>
                <v-btn text color="#F26522" class="ism-dp-btn ism-dp-btn--ok" @click="dateMenu = false">OK</v-btn>
              </div>
            </div>
          </v-menu>
          <div v-if="serverSideErrors.send_at" style="color:var(--v-error-base); font-size:12px; margin-top:4px;">{{ serverSideErrors.send_at }}</div>
        </div>
      </div>
    </PremiumModalForm>

    <!-- Delete Confirmation Dialog -->
    <ConfirmModal
      :isOpen="deleteDialog"
      type="danger"
      title="Delete Broadcast"
      message="Are you sure you want to delete this broadcast? This will also remove the notification from all members' devices."
      confirmText="Delete"
      confirmColor="error"
      @confirm="deleteBroadcast"
      @cancel="deleteDialog = false"
    />
  </div>
</template>

<script>
import { mapMutations } from 'vuex';
import PremiumTable from '../common/PremiumTable.vue';
import PremiumModalForm from '../common/PremiumModalForm.vue';
import PremiumInput from '../common/PremiumInput.vue';
import ConfirmModal from '../common/ConfirmModal.vue';

export default {
  name: 'Broadcasts',
  components: { PremiumTable, PremiumModalForm, PremiumInput, ConfirmModal },

  data() {
    return {
      loading: true,
      saving: false,
      formDialog: false,
      deleteDialog: false,
      deleteId: null,
      isEditing: false,
      scheduleDate: null,
      scheduleTime: null,
      broadcasts: [],
      societies: [],
      serverSideErrors: {},
      editId: null,
      form: {
        title: '',
        description: '',
        target_type: 'all',
        society_ids: [],
        delivery_type: 'instant',
        send_at: null,
      },
      targetOptions: [
        { label: 'All Societies', value: 'all', icon: 'mdi-account-group-outline' },
        { label: 'Specific Society', value: 'specific', icon: 'mdi-home-city-outline' },
      ],
      deliveryOptions: [
        { label: 'Send Now', value: 'instant', icon: 'mdi-lightning-bolt' },
        { label: 'Schedule', value: 'scheduled', icon: 'mdi-clock-outline' },
      ],
      headers: [
        { text: 'Title',           value: 'title',          width: '20%' , align: 'start' },
        { text: 'Societies',       value: 'target_type',    width: '30%' },
        { text: 'Notification Type',   value: 'delivery_type',  width: '10%' },
        // { text: 'Status',          value: 'status',         width: '11%' },
        { text: 'Date & Time',     value: 'send_at',        width: '12%' },
        { text: 'Created At',      value: 'created_at',     width: '12%' },
        { text: 'Actions',         value: 'actions',        width: '5%', sortable: false, align: 'right' },
      ],
    };
  },

  computed: {
    minDateOnly() {
      return new Date().toISOString().slice(0, 10);
    },
    formattedScheduleDate() {
      if (!this.scheduleDate) return '';
      return new Date(this.scheduleDate).toLocaleDateString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric'
      });
    },
    formattedScheduleTime() {
      if (!this.scheduleTime) return '';
      const [hours, minutes] = this.scheduleTime.split(':');
      const h = parseInt(hours, 10);
      const ampm = h >= 12 ? 'PM' : 'AM';
      const formattedHours = h % 12 || 12;
      return `${formattedHours < 10 ? '0' + formattedHours : formattedHours}:${minutes} ${ampm}`;
    }
  },

  methods: {
    ...mapMutations('alert', ['setAlert']),

    formatDate(val) {
      if (!val) return '—';
      return new Date(val).toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    },

    statusColor(s) {
      return { sent: 'green lighten-5', pending: 'amber lighten-5', failed: 'red lighten-5' }[s] || 'grey lighten-4';
    },
    statusTextColor(s) {
      return { sent: 'green darken-3', pending: 'amber darken-4', failed: 'red darken-3' }[s] || 'grey darken-2';
    },
    statusIcon(s) {
      return { sent: 'mdi-check-circle-outline', pending: 'mdi-timer-sand', failed: 'mdi-close-circle-outline' }[s] || 'mdi-help-circle-outline';
    },

    getBroadcasts() {
      this.loading = true;
      this.$axios.get('superadmin/broadcasts').then(res => {
        this.loading = false;
        const d = res.data;
        if (d.status !== false) {
          this.broadcasts = d.data || d.broadcasts || d || [];
        } else {
          this.setAlert({ showAlert: true, variant: 'red accent-3', timeout: 3000, message: d.message || 'Failed to load broadcasts.' });
        }
      }).catch(err => {
        this.loading = false;
        this.setAlert({ showAlert: true, variant: 'red accent-3', timeout: 3000, message: err.response?.data?.message || 'Failed to load broadcasts.' });
      });
    },

    getSocieties() {
      this.$axios.get('superadmin/society').then(res => {
        const d = res.data;
        const list = d.socities || d.societies || d.data || [];
        this.societies = list.map(s => ({ ...s, id: String(s.id || s._id) }));
      }).catch(() => {});
    },

    openForm(item) {
      if (item && item._id) {
        this.isEditing = true;
        this.editId = item._id;
        this.form = {
          title: item.title,
          description: item.description,
          target_type: item.target_type,
          society_ids: item.society_ids ? item.society_ids.map(s => String(s.id || s._id || s)) : [],
          delivery_type: item.delivery_type,
          send_at: item.send_at
        };
        if (item.delivery_type === 'scheduled' && item.send_at) {
           const dt = new Date(item.send_at);
           const tzOffset = dt.getTimezoneOffset() * 60000;
           const localISOTime = (new Date(dt - tzOffset)).toISOString();
           this.scheduleDate = localISOTime.slice(0, 10);
           this.scheduleTime = localISOTime.slice(11, 16);
        } else {
           this.scheduleDate = null;
           this.scheduleTime = null;
        }
      } else {
        this.isEditing = false;
        this.editId = null;
        this.form = { title: '', description: '', target_type: 'all', society_ids: [], delivery_type: 'instant', send_at: null };
        this.scheduleDate = null;
        this.scheduleTime = null;
      }
      this.serverSideErrors = {};
      this.$validator.reset();
      this.formDialog = true;
    },

    saveBroadcast() {
      this.$validator.validateAll().then(valid => {
        if (!valid) return;

        // Extra validation for scheduled time
        if (this.form.delivery_type === 'scheduled') {
          if (!this.scheduleDate || !this.scheduleTime) {
             this.$set(this.serverSideErrors, 'send_at', 'Schedule date and time are required');
             return;
          }
          const dt = new Date(`${this.scheduleDate}T${this.scheduleTime}`);
          if (dt <= new Date()) {
             this.$set(this.serverSideErrors, 'send_at', 'Scheduled time must be in the future');
             return;
          }
          this.form.send_at = dt.toISOString();
        }

        this.saving = true;
        this.serverSideErrors = {};

        const payload = { ...this.form };
        if (payload.delivery_type === 'instant') {
          payload.send_at = new Date().toISOString();
        }

        const method = this.isEditing ? 'put' : 'post';
        const url = this.isEditing ? `superadmin/broadcasts/${this.editId}` : 'superadmin/broadcasts';

        this.$axios[method](url, payload).then(res => {
          this.saving = false;
          const d = res.data;
          if (d && d.status === false) {
            this.setAlert({ showAlert: true, variant: 'red accent-3', timeout: 4000, message: d.message || 'Failed to save broadcast.' });
            return;
          }
          this.formDialog = false;
          this.getBroadcasts();
          const actionWord = this.isEditing ? 'updated' : 'sent';
          const msg = this.form.delivery_type === 'instant'
            ? `Broadcast ${actionWord} successfully!`
            : `Broadcast scheduled successfully!`;
          this.setAlert({ showAlert: true, variant: 'green accent-4', timeout: 3000, message: msg });
        }).catch(err => {
          this.saving = false;
          const d = err?.response?.data;
          const msg = d?.message || d?.error || err?.message || 'Something went wrong.';
          this.setAlert({ showAlert: true, variant: 'red accent-3', timeout: 4000, message: msg });
          if (d?.errors && Array.isArray(d.errors)) {
            d.errors.forEach(e => { this.$set(this.serverSideErrors, e.field, e.message); });
          }
        });
      });
    },

    confirmDelete(item) {
      this.deleteId = item._id || item.id;
      this.deleteDialog = true;
    },

    deleteBroadcast() {
      this.deleteDialog = false;
      this.$axios.delete(`superadmin/broadcasts/${this.deleteId}`).then(res => {
        const d = res.data;
        if (d && d.status === false) {
          this.setAlert({ showAlert: true, variant: 'red accent-3', timeout: 4000, message: d.message || 'Failed to delete broadcast.' });
          return;
        }
        this.getBroadcasts();
        this.setAlert({ showAlert: true, variant: 'green accent-4', timeout: 3000, message: 'Broadcast deleted successfully!' });
      }).catch(err => {
        const d = err?.response?.data;
        const msg = d?.message || d?.error || err?.message || 'Something went wrong while deleting.';
        this.setAlert({ showAlert: true, variant: 'red accent-3', timeout: 4000, message: msg });
      });
    },
  },

  mounted() {
    this.getBroadcasts();
    this.getSocieties();
  },
};
</script>

<style scoped>
::v-deep .premium-input-wrapper {
  margin-bottom: 0 !important;
}

.form-section-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary, #64748b);
  letter-spacing: 0.02em;
}

.target-option {
  display: flex;
  align-items: center;
  padding: 10px 20px;
  border-radius: 10px;
  border: 2px solid var(--border-color, #e2e8f0);
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
  color: var(--text-secondary, #64748b);
  flex: 1;
  justify-content: center;
}

.target-option:hover {
  border-color: var(--accent, #0d9972);
  color: var(--accent, #0d9972);
}

.target-option--active {
  border-color: var(--accent, #0d9972);
  background: rgba(13, 153, 114, 0.08);
  color: var(--accent, #0d9972);
  font-weight: 600;
}

/* ── Datepicker ── */
.ism-custom-datepicker-menu {
  border-radius: 16px !important;
  box-shadow: 0 12px 40px rgba(0,0,0,0.13) !important;
  border: 1px solid var(--border-color, #e2e8f0) !important;
  overflow: hidden;
}
.ism-custom-datepicker {
  font-family: 'Inter', sans-serif !important;
  border-radius: 16px !important;
}
::v-deep .v-date-picker-header { padding: 16px 14px 8px; background: transparent !important; }
::v-deep .v-date-picker-header .v-btn .v-icon { color: var(--text-secondary, #64748b) !important; }
::v-deep .v-date-picker-header .v-btn:hover .v-icon { color: #F26522 !important; }
::v-deep .v-date-picker-header__value button { font-weight: 800 !important; color: var(--text-primary, #1e293b) !important; font-size: 15px !important; }
::v-deep .v-date-picker-table { padding: 0 12px 8px 12px !important; }
::v-deep .v-date-picker-table th { font-weight: 700 !important; color: var(--text-secondary, #64748b) !important; font-size: 11px !important; text-transform: uppercase !important; }
::v-deep .v-date-picker-table .v-btn { font-weight: 600 !important; border-radius: 8px !important; color: var(--text-primary, #1e293b) !important; }
::v-deep .v-date-picker-table .v-btn:not(.v-btn--active):hover { color: #F26522 !important; }
::v-deep .v-date-picker-table .v-btn:not(.v-btn--active):hover::before { background: rgba(242,101,34,0.08) !important; opacity: 1 !important; }
::v-deep .v-date-picker-table .v-btn--active { background-color: #F26522 !important; color: #fff !important; box-shadow: 0 4px 12px rgba(242,101,34,0.35) !important; }
::v-deep .v-picker__actions { padding: 8px 12px 12px !important; border-top: 1px solid var(--border-color, #e2e8f0) !important; }
.ism-dp-btn { text-transform: none !important; font-weight: 700 !important; font-size: 13px !important; border-radius: 8px !important; }
.ism-dp-btn--ok { background: rgba(242,101,34,0.08) !important; margin-left: 4px; }
</style>

<style>
body.dark-mode .ism-custom-datepicker-menu,
.theme--dark .ism-custom-datepicker-menu {
  box-shadow: 0 16px 48px rgba(0,0,0,0.55) !important;
  border-color: rgba(255,255,255,0.1) !important;
}
body.dark-mode .ism-custom-datepicker-menu .v-picker,
body.dark-mode .ism-custom-datepicker-menu .v-picker__body,
.theme--dark .ism-custom-datepicker-menu .v-picker,
.theme--dark .ism-custom-datepicker-menu .v-picker__body { background: #1e293b !important; }
body.dark-mode .ism-custom-datepicker-menu .v-card,
.theme--dark .ism-custom-datepicker-menu .v-card { background: #1e293b !important; }
body.dark-mode .ism-custom-datepicker-menu .v-date-picker-header__value button,
.theme--dark .ism-custom-datepicker-menu .v-date-picker-header__value button { color: #f1f5f9 !important; font-weight: 800 !important; }
body.dark-mode .ism-custom-datepicker-menu .v-date-picker-header .v-btn .v-icon,
.theme--dark .ism-custom-datepicker-menu .v-date-picker-header .v-btn .v-icon { color: #94a3b8 !important; }
body.dark-mode .ism-custom-datepicker-menu .v-date-picker-header .v-btn:hover .v-icon,
.theme--dark .ism-custom-datepicker-menu .v-date-picker-header .v-btn:hover .v-icon { color: #f1f5f9 !important; }
body.dark-mode .ism-custom-datepicker-menu .v-date-picker-table th,
.theme--dark .ism-custom-datepicker-menu .v-date-picker-table th { color: #64748b !important; font-weight: 700 !important; }
body.dark-mode .ism-custom-datepicker-menu .v-date-picker-table .v-btn,
.theme--dark .ism-custom-datepicker-menu .v-date-picker-table .v-btn { color: #cbd5e1 !important; font-weight: 600 !important; }
body.dark-mode .ism-custom-datepicker-menu .v-date-picker-table .v-btn.v-btn--outlined,
.theme--dark .ism-custom-datepicker-menu .v-date-picker-table .v-btn.v-btn--outlined { border-color: #F26522 !important; color: #F26522 !important; }
body.dark-mode .ism-custom-datepicker-menu .v-date-picker-table .v-btn:not(.v-btn--active):hover::before,
.theme--dark .ism-custom-datepicker-menu .v-date-picker-table .v-btn:not(.v-btn--active):hover::before { background: rgba(242,101,34,0.15) !important; opacity: 1 !important; }
body.dark-mode .ism-custom-datepicker-menu .v-date-picker-table .v-btn:not(.v-btn--active):hover,
.theme--dark .ism-custom-datepicker-menu .v-date-picker-table .v-btn:not(.v-btn--active):hover { color: #F26522 !important; }
body.dark-mode .ism-custom-datepicker-menu .v-date-picker-table .v-btn--active,
.theme--dark .ism-custom-datepicker-menu .v-date-picker-table .v-btn--active { background-color: #F26522 !important; color: #ffffff !important; box-shadow: 0 4px 14px rgba(242,101,34,0.45) !important; }
body.dark-mode .ism-custom-datepicker-menu .v-date-picker-table .v-btn.v-btn--disabled,
.theme--dark .ism-custom-datepicker-menu .v-date-picker-table .v-btn.v-btn--disabled { color: #334155 !important; }
body.dark-mode .ism-custom-datepicker-menu .v-picker__actions,
.theme--dark .ism-custom-datepicker-menu .v-picker__actions { background: #1e293b !important; border-top: 1px solid rgba(255,255,255,0.07) !important; }
body.dark-mode .ism-custom-datepicker-menu .ism-dp-btn,
.theme--dark .ism-custom-datepicker-menu .ism-dp-btn { color: #94a3b8 !important; }
body.dark-mode .ism-custom-datepicker-menu .ism-dp-btn.ism-dp-btn--ok,
.theme--dark .ism-custom-datepicker-menu .ism-dp-btn.ism-dp-btn--ok { color: #F26522 !important; background: rgba(242,101,34,0.1) !important; }
</style>
